import { ChangeDetectionStrategy, Component } from '@angular/core';
import { formatISO, isBefore, startOfDay } from 'date-fns';
import { PublicationsRepositoryService } from '../../services/publications-repository.service';
import { CommonModule } from '@angular/common';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { FormsModule } from '@angular/forms';
import { CleaveDirective } from '@shared/directives/cleave.directive';
import { DateBarPipe } from '../../pipes/dates-bar.pipe';
import { distinctUntilKeyChanged, map, take } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PublicationSearchFilter } from '../../types/publications.types';

@Component({
  selector: 'app-publications-dates-bar',
  templateUrl: './dates-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NzDatePickerModule,
    FormsModule,
    DateBarPipe,
    CommonModule,
    CleaveDirective,
  ],
})
export class DatesBarComponent {
  fromDate$ = this.repository.filters$.pipe(
    distinctUntilKeyChanged('fromDate'),
    map((filters: PublicationSearchFilter) => filters.fromDate)
  );

  toDate$ = this.repository.filters$.pipe(
    distinctUntilKeyChanged('toDate'),
    map((filters: PublicationSearchFilter) => filters.toDate)
  );

  constructor(public repository: PublicationsRepositoryService) {}

  onChangeFromDate(value: Date | null): void {
    const fromDate: Record<string, string | null> = {
      fromdate: value ? formatISO(value, { representation: 'date' }) : value,
    };

    this.repository.setQueryParamsUrl(fromDate);
  }

  onChangeToDate(value: Date | null): void {
    const toDate: Record<string, string | null> = {
      todate: value ? formatISO(value, { representation: 'date' }) : value,
    };

    this.repository.setQueryParamsUrl(toDate);
  }

  disabledFromDate = (current: Date): boolean => {
    let isDisabledDate = false;

    this.repository.filters$
      .pipe(
        take(1),
        tap(({ toDate }): void => {
          if (toDate) {
            const isAfterToDate =
              startOfDay(current) >= startOfDay(new Date(toDate || ''));
            isDisabledDate = isAfterToDate || this.isFutureDate(current);
          } else {
            isDisabledDate = this.isFutureDate(current);
          }
        })
      )
      .subscribe();

    return isDisabledDate;
  };

  disabledToDate = (current: Date): boolean => {
    let isDisabledDate = false;

    this.repository.filters$
      .pipe(
        take(1),
        tap(({ fromDate }): void => {
          if (fromDate) {
            const isBeforeFromDate =
              startOfDay(current) <= startOfDay(new Date(fromDate));

            isDisabledDate = isBeforeFromDate || this.isFutureDate(current);
          } else {
            isDisabledDate = this.isFutureDate(current);
          }
        })
      )
      .subscribe();

    return isDisabledDate;
  };

  private isFutureDate(current: Date): boolean {
    return isBefore(startOfDay(new Date()), startOfDay(current));
  }
}
